import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signInAnonymously, signInWithCustomToken, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, collection, addDoc, onSnapshot, query, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { 
  Clipboard, Save, Plus, CheckCircle, 
  BarChart3, Video, Layout, Activity, Printer, Sun, Lock, Unlock, 
  ShieldCheck, Target, Zap, Share2, Copy, EyeOff, FileText, Globe, HardDrive, FileVideo, Upload, Loader2, Trash2, AlertTriangle, ChevronRight, Info
} from 'lucide-react';

// Firebase config injected here
const __firebase_config = JSON.stringify({
  apiKey: 'AIzaSyDuazJHS5n8EJSrky_cwKwmXKXFVaLeKsE',
  authDomain: 'video-feedback-framework.firebaseapp.com',
  projectId: 'video-feedback-framework',
  storageBucket: 'video-feedback-framework.firebasestorage.app',
  messagingSenderId: '940490646218',
  appId: '1:940490646218:web:ab75e7a9aaf7c6dfab8a33',
  measurementId: 'G-9NMY5Q42TJ'
});
const __app_id = 'video-feedback-framework';
const __initial_auth_token = undefined;

// ==============================
// DRAWER START – Side Panel Quick View
// ==============================
function ResultDrawer({
  entry,
  onClose,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  isLiteView
}) {
  const drawerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(!!entry);
  }, [entry]);

  // Focus trap + ESC
  useEffect(() => {
    if (!entry) return;
    const drawerEl = drawerRef.current;
    if (!drawerEl) return;

    const focusable = drawerEl.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }

      if (e.key === 'Tab' && focusable.length > 0) {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [entry, onClose]);

  if (!entry) return null;

  const scores = entry.scores || {};
  const score = (value) => (typeof value === 'number' ? value : '—');

  return (
    <>
      <div
        className={`fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      <div
        ref={drawerRef}
        className={`fixed top-0 right-0 h-full w-full z-[70] bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl transform transition-all duration-300 ease-out ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'}`}
        style={{ maxHeight: '100dvh', maxWidth: 'min(100vw, 38rem)', width: '100%' }}
      >
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
            <h2 className="text-lg font-semibold">Test ID: {entry.testId || '—'}</h2>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-800 dark:hover:text-white"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-6 text-sm text-slate-700 dark:text-slate-200">
            <section className="space-y-1">
              <h3 className="font-semibold mb-1">Test Setup</h3>
              <div>Objective: {entry.testObjective || '—'}</div>
              {!isLiteView && <div>Model / Tool: {entry.toolVersion || '—'}</div>}
              <div>Date: {entry.date || '—'}</div>
              {!isLiteView && <div>Iterations: {entry.iterations ?? '—'}</div>}
              <div>Viability: {entry.viability || '—'}</div>
              <div>Clip Length: {entry.clipLength || '—'}</div>
              {!isLiteView && <div>Visibility: {entry.visibility || '—'}</div>}
              {!isLiteView && <div>Image Set: {entry.imageSetId || '—'}</div>}
            </section>

            {!isLiteView && (
              <section className="space-y-1">
                <h3 className="font-semibold mb-1">Scores</h3>
                <div>Visual Fidelity: {score(scores.visualFidelity)}</div>
                <div>Temporal Consistency: {score(scores.temporalConsistency)}</div>
                <div>Prompt Adherence: {score(scores.promptAdherence)}</div>
                <div>Overall Usability: {score(scores.overallUsability)}</div>
                <div>
                  Average: {
                    typeof scores.visualFidelity === 'number' &&
                    typeof scores.temporalConsistency === 'number' &&
                    typeof scores.promptAdherence === 'number' &&
                    typeof scores.overallUsability === 'number'
                      ? (((scores.visualFidelity + scores.temporalConsistency + scores.promptAdherence + scores.overallUsability) / 4).toFixed(1))
                      : '—'
                  }
                </div>
              </section>
            )}

            <section className="space-y-3">
              <h3 className="font-semibold mb-1">Logs</h3>
              {!isLiteView && (
                <>
                  <div>
                    <div className="font-medium">What worked</div>
                    <div className="text-slate-600 dark:text-slate-300">{entry.workedWell || '—'}</div>
                  </div>
                  <div>
                    <div className="font-medium">What broke</div>
                    <div className="text-slate-600 dark:text-slate-300">{entry.issues || '—'}</div>
                  </div>
                </>
              )}
              <div>
                <div className="font-medium">External summary</div>
                <div className="text-slate-600 dark:text-slate-300">{entry.externalSummaryText || '—'}</div>
              </div>
            </section>

            {entry.assets?.length > 0 && (
              <section className="space-y-3">
                <h3 className="font-semibold mb-1">Assets</h3>
                <div className="space-y-4">
                  {entry.assets.map((asset, i) => (
                    asset.type === 'video' ? (
                      <video
                        key={i}
                        src={asset.downloadURL}
                        controls
                        className="w-full rounded-lg bg-black"
                      />
                    ) : (
                      <img
                        key={i}
                        src={asset.thumbnailURL || asset.downloadURL}
                        alt={asset.name || `asset-${i}`}
                        className="w-full rounded-lg border border-slate-200 dark:border-slate-800"
                      />
                    )
                  ))}
                </div>
              </section>
            )}
          </div>

          <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-between">
            <button
              onClick={onPrevious}
              disabled={!hasPrevious}
              className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={onNext}
              disabled={!hasNext}
              className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
// ==============================
// DRAWER END
// ==============================

// --- Firebase Configuration & Initialization ---
const firebaseConfig = JSON.parse(__firebase_config);
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const appId = typeof __app_id !== 'undefined' ? __app_id : 'video-feedback-framework'; 

const App = () => {
  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null); 
  const [isDeleting, setIsDeleting] = useState(false);

  // DRAWER STATE
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Security State
  const [userPermission, setUserPermission] = useState(null); // 'full' or 'lite'
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // View Control
  const isLiteView = new URLSearchParams(window.location.search).get('view') === 'lite';
  
  // Determine if authenticated based on user and their permission
  const isAuthenticated = user && userPermission;
  const isLiteAuthenticated = isAuthenticated && (userPermission === 'lite' || userPermission === 'full');
  const isFullAuthenticated = isAuthenticated && userPermission === 'full';

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark' || (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  const initialFormState = {
    testId: `TEST-${Math.floor(1000 + Math.random() * 9000)}`,
    date: new Date().toISOString().split('T')[0],
    toolVersion: '',
    testObjective: '',
    prompt: '', 
    internalNotes: '', 
    iterations: 1,
    visibility: 'internal',
    viability: 'Light Editing',
    imageSetId: '',
    status: 'pass',
    clipLength: '15s',
    scores: { 
      visualFidelity: 0, 
      temporalConsistency: 0, 
      promptAdherence: 0, 
      overallUsability: 0 
    },
    workedWell: '',
    issues: '',
    assets: [],
    externalSummaryText: ''
  };
  const [formData, setFormData] = useState(initialFormState);

  // Validation Logic for the Log Test Result Page
  const canApprove = 
    formData.testObjective.trim() !== '' &&
    formData.iterations > 0 &&
    formData.workedWell.trim() !== '' &&
    formData.issues.trim() !== '' &&
    formData.scores.visualFidelity > 0 &&
    formData.scores.temporalConsistency > 0 &&
    formData.scores.promptAdherence > 0 &&
    formData.scores.overallUsability > 0 &&
    formData.viability !== '' &&
    formData.assets.some(a => a.marked_for_external);

  // Derived Fields for Lite Preview
  const computedOverallScore = (
    (formData.scores.visualFidelity + 
     formData.scores.temporalConsistency + 
     formData.scores.promptAdherence + 
     formData.scores.overallUsability) / 4
  ).toFixed(1);

  const getSuitabilityLabel = () => {
    if (formData.viability === 'As-is') return 'Promising';
    if (formData.viability === 'Not Usable') return 'Unviable';
    return 'Conditional';
  };

  // --- Helper: Generate Video Thumbnail ---
  const generateVideoThumbnail = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = URL.createObjectURL(file);
      video.muted = true;
      video.playsInline = true;

      video.onloadedmetadata = () => {
        video.currentTime = Math.min(video.duration, 1);
      };

      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob((blob) => {
          resolve(blob);
          URL.revokeObjectURL(video.src);
        }, 'image/jpeg', 0.85);
      };

      video.onerror = () => {
        resolve(null);
        URL.revokeObjectURL(video.src);
      };
    });
  };

  useEffect(() => {
    document.title = "Phase 1 Beta Framework";
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/svg+xml';
    link.rel = 'shortcut icon';
    link.href = `data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%234f46e5%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22><path d=%22M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10%22/><path d=%22m9 12 2 2 4-4%22/></svg>`;
    document.getElementsByTagName('head')[0].appendChild(link);
  }, []);

  useEffect(() => {
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u && u.email) {
        // Check user permissions in Firestore
        try {
          const userDocRef = doc(db, 'users', u.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const permission = userDoc.data().permission || 'lite';
            setUserPermission(permission);
          } else {
            // Default to lite if no permission set
            setUserPermission('lite');
          }
        } catch (error) {
          console.error('Error fetching user permissions:', error);
          setUserPermission('lite');
        }
      } else {
        setUserPermission(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || !userPermission) return;
    
    // Check permissions based on view
    if (isLiteView && !isLiteAuthenticated) return;
    if (!isLiteView && !isFullAuthenticated) return;

    const q = collection(db, 'artifacts', appId, 'public', 'data', 'feedback_entries');
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sorted = data.sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date));
      
      if (isLiteView) {
        setEntries(sorted.filter(e => e.visibility === 'external').map(e => ({
          ...e,
          prompt: undefined, 
          internalNotes: undefined, 
          iterations: undefined, 
          userName: "Authorized Tester",
          assets: (e.assets || []).filter(asset => asset.marked_for_external === true)
        })));
      } else {
        setEntries(sorted.reverse());
      }
    }, (error) => console.error(error));
    return () => unsubscribe();
  }, [user, userPermission, isLiteView, isLiteAuthenticated, isFullAuthenticated]);

  const handleAdminAuth = async (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setAuthError('');
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, emailInput, passwordInput);
      const user = userCredential.user;
      
      // Fetch user permission
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const permission = userDoc.data().permission || 'lite';
        setUserPermission(permission);
        
        // Check if user has appropriate access for the view
        if (isLiteView && (permission === 'lite' || permission === 'full')) {
          // Lite view accessible to both lite and full users
        } else if (!isLiteView && permission !== 'full') {
          // Full view only accessible to full users
          await signOut(auth);
          setAuthError('You do not have permission to access the full dashboard.');
          setIsAuthenticating(false);
          return;
        }
      } else {
        await signOut(auth);
        setAuthError('User account not properly configured.');
        setIsAuthenticating(false);
        return;
      }
    } catch (error) {
      console.error('Authentication error:', error);
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        setAuthError('Invalid email or password.');
      } else if (error.code === 'auth/invalid-email') {
        setAuthError('Invalid email format.');
      } else if (error.code === 'auth/too-many-requests') {
        setAuthError('Too many failed attempts. Please try again later.');
      } else {
        setAuthError('Authentication failed. Please try again.');
      }
    }
    
    setIsAuthenticating(false);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUserPermission(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleAssetUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    setIsUploading(true);
    try {
      const fileId = Math.random().toString(36).substr(2, 9);
      const isVideo = file.type.startsWith('video');
      
      const storagePath = `artifacts/${appId}/public/assets/${fileId}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      let thumbnailURL = downloadURL;
      let thumbnailPath = null;

      if (isVideo) {
        const thumbBlob = await generateVideoThumbnail(file);
        if (thumbBlob) {
          thumbnailPath = `artifacts/${appId}/public/assets/thumb_${fileId}.jpg`;
          const thumbRef = ref(storage, thumbnailPath);
          await uploadBytes(thumbRef, thumbBlob);
          thumbnailURL = await getDownloadURL(thumbRef);
        }
      }

      const newAsset = {
        id: `ASSET-${fileId}`,
        type: isVideo ? 'video' : 'image',
        name: file.name,
        filePath: storagePath,
        thumbnailPath: thumbnailPath,
        downloadURL: downloadURL,
        thumbnailURL: thumbnailURL, 
        marked_for_external: false,
        uploadedAt: new Date().toISOString()
      };

      setFormData(prev => ({
        ...prev,
        assets: [...prev.assets, newAsset]
      }));
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!deleteId || isDeleting) return;
    setIsDeleting(true);

    try {
      const entryToDelete = entries.find(e => e.id === deleteId);
      if (!entryToDelete) throw new Error("Entry not found");

      if (entryToDelete.assets && entryToDelete.assets.length > 0) {
        for (const asset of entryToDelete.assets) {
          if (asset.filePath) {
            try {
              const mainRef = ref(storage, asset.filePath);
              await deleteObject(mainRef);
            } catch (err) { console.warn("Could not delete main file:", asset.filePath); }
          }
          if (asset.thumbnailPath) {
            try {
              const thumbRef = ref(storage, asset.thumbnailPath);
              await deleteObject(thumbRef);
            } catch (err) { console.warn("Could not delete thumbnail:", asset.thumbnailPath); }
          }
        }
      }

      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'feedback_entries', deleteId));
      setDeleteId(null);
    } catch (error) {
      console.error("Deletion failed:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleAssetExternal = (assetId) => {
    setFormData(prev => ({
      ...prev,
      assets: prev.assets.map(a => 
        a.id === assetId ? { ...a, marked_for_external: !a.marked_for_external } : a
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || isLiteView) return;
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'feedback_entries'), {
        ...formData,
        userId: user.uid,
        userName: user.isAnonymous ? "Tester" : (user.displayName || user.uid),
        createdAt: new Date().toISOString()
      });
      setFormData(initialFormState);
      setActiveTab('dashboard');
    } catch (error) { console.error(error); }
    finally { setIsSubmitting(false); }
  };

  const toggleVisibility = async (entry) => {
    if (isLiteView) return;
    const newVisibility = entry.visibility === 'external' ? 'internal' : 'external';
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'feedback_entries', entry.id), { visibility: newVisibility });
  };

  const copyToClipboard = (text, label) => {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    setCopySuccess(label);
    setTimeout(() => setCopySuccess(''), 2000);
  };

  const stats = {
    total: entries.length,
    viable: entries.filter(e => e.viability === 'As-is').length,
    avgIterations: entries.length > 0 ? (entries.reduce((acc, curr) => acc + (Number(curr.iterations) || 0), 0) / entries.length).toFixed(1) : 0,
    viabilityRate: entries.length > 0 ? Math.round((entries.filter(e => e.viability === 'As-is').length / entries.length) * 100) : 0
  };

  const canonicalPrompts = [
    { id: '01', title: 'Static Baseline', text: 'A single adult person standing still, facing the camera. Neutral expression. Indoor setting with simple, even lighting. Medium shot from the waist up. Minimal movement. Approximately 10–15 seconds.' },
    { id: '02', title: 'Subtle Motion', text: 'A single adult person standing and gently shifting their weight from one foot to the other. Neutral expression. Indoor setting with consistent lighting. Medium shot. Smooth, natural movement. Approximately 10–15 seconds.' },
    { id: '03', title: 'Walking', text: 'A single adult person walking slowly forward at a natural pace. Simple indoor or controlled outdoor environment. Full-body shot. Camera remains steady. Approximately 10–20 seconds.' },
    { id: '04', title: 'Turn / Rotation', text: 'A single adult person standing and slowly turning their body from facing forward to a three-quarter angle. Neutral expression. Simple indoor environment. Medium shot. Approximately 10–15 seconds.' },
    { id: '05', title: 'Close-up', text: 'Close-up of a single adult person’s face. Neutral expression. Minimal head movement. Soft, even lighting. Camera remains still. Approximately 10–15 seconds.' }
  ];

  const getAverageScore = (entry) => {
    const s = entry?.scores || {};
    if (
      typeof s.visualFidelity === 'number' &&
      typeof s.temporalConsistency === 'number' &&
      typeof s.promptAdherence === 'number' &&
      typeof s.overallUsability === 'number'
    ) {
      return (((s.visualFidelity + s.temporalConsistency + s.promptAdherence + s.overallUsability) / 4).toFixed(1));
    }
    return '—';
  };

  // DRAWER HELPERS
  const openDrawer = (entry, index) => {
    setSelectedEntry(entry);
    setSelectedIndex(index);
  };

  const closeDrawer = () => {
    setSelectedEntry(null);
    setSelectedIndex(null);
  };

  const goNext = () => {
    if (selectedIndex < entries.length - 1) {
      const i = selectedIndex + 1;
      setSelectedIndex(i);
      setSelectedEntry(entries[i]);
    }
  };

  const goPrevious = () => {
    if (selectedIndex > 0) {
      const i = selectedIndex - 1;
      setSelectedIndex(i);
      setSelectedEntry(entries[i]);
    }
  };

  // DRAWER KEYBOARD NAVIGATION
  useEffect(() => {
    if (!selectedEntry || isLiteView) return;

    const handler = (e) => {
      if (e.key === 'Escape') closeDrawer();
      if (e.key === 'ArrowDown') goNext();
      if (e.key === 'ArrowUp') goPrevious();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedEntry, selectedIndex, isLiteView]);

  // (intentionally no history manipulation for drawer)

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950"><Activity className="animate-spin text-indigo-600" /></div>;

  // Authentication check for both views
  const requiresAuth = (isLiteView && !isLiteAuthenticated) || (!isLiteView && !isFullAuthenticated);

  if (requiresAuth) {
    return (
      <div className={darkMode ? 'dark' : ''}>
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6">
          <form onSubmit={handleAdminAuth} className="w-full max-w-md bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800">
            <div className="text-center mb-6">
              <Lock size={32} className={`mx-auto mb-4 ${isLiteView ? 'text-emerald-600' : 'text-indigo-600'}`} />
              <h2 className="text-2xl font-bold dark:text-white mb-2">
                {isLiteView ? 'Lite Viewer Access' : 'Phase 1 Framework Access'}
              </h2>
              <p className="text-xs text-slate-500">
                {isLiteView ? 'Sign in to view shared results' : 'Internal dashboard - authorized users only'}
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Email</label>
                <input 
                  type="email" 
                  placeholder="your.email@example.com" 
                  value={emailInput} 
                  onChange={(e) => setEmailInput(e.target.value)} 
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                  disabled={isAuthenticating}
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Password</label>
                <input 
                  type="password" 
                  placeholder="Enter your password" 
                  value={passwordInput} 
                  onChange={(e) => setPasswordInput(e.target.value)} 
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                  disabled={isAuthenticating}
                />
              </div>
            </div>
            
            {authError && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-600 dark:text-red-400 text-xs">{authError}</p>
              </div>
            )}
            
            <button 
              type="submit" 
              disabled={isAuthenticating}
              className={`w-full mt-6 ${isLiteView ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-indigo-600 hover:bg-indigo-700'} text-white py-3 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isAuthenticating ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
            
            {!isLiteView && (
              <button 
                type="button" 
                onClick={() => window.location.search = '?view=lite'} 
                className="w-full mt-4 text-xs text-emerald-400 font-bold hover:underline"
                disabled={isAuthenticating}
              >
                Switch to Lite Viewer
              </button>
            )}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={darkMode ? 'dark' : ''}>
      <style>{`
        #print-summary { display: none; }
        @media print {
          body { background: white; color: #0f172a; }
          #app-shell { display: none !important; }
          #print-summary { display: block !important; }
          .print-row { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>

      <div id="app-shell" className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 pb-16 transition-colors font-sans">
        
        {/* Delete Confirmation Overlay */}
        {deleteId && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-6">
            <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-3xl p-8 shadow-2xl border border-slate-200 dark:border-slate-800 text-center">
              <div className="bg-red-100 dark:bg-red-900/30 w-16 h-16 rounded-full flex items-center justify-center text-red-600 mx-auto mb-6">
                <AlertTriangle size={32} />
              </div>
              <h3 className="text-xl font-black mb-2">Delete Entry?</h3>
              <p className="text-slate-500 text-sm mb-8 leading-relaxed">
                This will permanently remove the record and all associated assets from Cloud Storage. This action cannot be undone.
              </p>
              <div className="flex flex-col gap-3">
                <button 
                  onClick={handleDeleteEntry}
                  disabled={isDeleting}
                  className="w-full bg-red-600 text-white py-3 rounded-xl font-bold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                >
                  {isDeleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                  {isDeleting ? 'Deleting Files...' : 'Yes, Delete Everything'}
                </button>
                <button 
                  onClick={() => setDeleteId(null)}
                  disabled={isDeleting}
                  className="w-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 py-3 rounded-xl font-bold hover:bg-slate-200 dark:hover:bg-slate-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header (Preserved) */}
        <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-50 px-6 py-4 print:hidden">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`${isLiteView ? 'bg-emerald-600' : 'bg-indigo-600'} p-2 rounded-lg text-white`}><ShieldCheck size={24} /></div>
              <div>
                <h1 className="text-xl font-bold tracking-tight">Phase 1 Beta Framework</h1>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest flex items-center gap-1 font-black">
                  {isLiteView ? 'Lite Shared Viewer' : 'Internal Performance Hub'} • {entries.length} Synced
                </p>
              </div>
            </div>
            {!isLiteView && (
              <nav className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                {[
                  { id: 'dashboard', label: 'Results', icon: BarChart3 }, 
                  { id: 'feedback', label: 'New Log', icon: Plus },
                  { id: 'plan', label: 'Workflow', icon: Layout },
                  { id: 'doc', label: 'Doc', icon: FileText }
                ].map((tab) => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-xs font-bold transition-all ${activeTab === tab.id ? 'bg-white dark:bg-slate-700 text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>
                    <tab.icon size={14} /> {tab.label}
                  </button>
                ))}
              </nav>
            )}
            <div className="flex gap-2">
              <button onClick={() => setDarkMode(!darkMode)} className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-500"><Sun size={18} /></button>
              {!isLiteView && <button onClick={handleLogout} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Unlock size={18} /></button>}
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto p-6">
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
                  <div className="bg-indigo-50 dark:bg-indigo-900/30 p-3 rounded-xl text-indigo-600"><Video size={20}/></div>
                  <div><p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Total Runs</p><h3 className="text-2xl font-black">{stats.total}</h3></div>
                </div>
                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
                  <div className="bg-emerald-50 dark:bg-emerald-900/30 p-3 rounded-xl text-emerald-600"><CheckCircle size={20}/></div>
                  <div><p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Viability Rate</p><h3 className="text-2xl font-black">{stats.viabilityRate}%</h3></div>
                </div>
                {!isLiteView && (
                  <>
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
                      <div className="bg-amber-50 dark:bg-amber-900/30 p-3 rounded-xl text-amber-600"><Zap size={20}/></div>
                      <div><p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Avg Iters</p><h3 className="text-2xl font-black">{stats.avgIterations}</h3></div>
                    </div>
                    <button onClick={() => copyToClipboard(`${window.location.origin}${window.location.pathname}?view=lite`, 'Lite Link')} className="bg-slate-900 text-white p-5 rounded-2xl flex items-center justify-between hover:opacity-90">
                      <div className="text-left">
                        <p className="text-[10px] opacity-60 font-bold uppercase">Share Restricted View</p>
                        <h3 className="text-xs font-bold">{copySuccess === 'Lite Link' ? 'Copied!' : 'Copy Developer Link'}</h3>
                      </div>
                      <Share2 size={18}/>
                    </button>
                  </>
                )}
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                  <h2 className="font-bold flex items-center gap-2"><Activity size={18} className="text-indigo-600"/> Result Repository</h2>
                  <div className="flex items-center gap-3">
                    <button onClick={() => window.print()} className="text-[10px] font-bold bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-lg flex items-center gap-2 transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"><Printer size={12}/> Print PDF</button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="bg-slate-50 dark:bg-slate-950/50 text-[10px] text-slate-500 font-black uppercase border-b border-slate-100 dark:border-slate-800">
                        <th className="px-6 py-4">ID / Set</th>
                        <th className="px-6 py-4">Objective</th>
                        <th className="px-6 py-4 text-center">Score</th>
                        <th className="px-6 py-4">Verdict</th>
                        <th className="px-6 py-4 text-center">Assets</th>
                        {!isLiteView && <th className="px-6 py-4 text-right">Actions</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {entries.map((entry, index) => (
                        <tr
                          key={entry.id}
                          className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 group cursor-pointer"
                          onClick={() => openDrawer(entry, index)}
                        >
                          <td className="px-6 py-4 font-mono text-[10px]">
                            <span className="text-indigo-600 font-black">{entry.testId}</span>
                            <div className="text-slate-400 mt-0.5">Set: {entry.imageSetId || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-bold text-xs">{entry.testObjective}</div>
                            <div className="text-[10px] text-slate-400 font-medium">{entry.toolVersion} • {entry.date}</div>
                          </td>
                          <td className="px-6 py-4 text-center font-black text-indigo-600">
                            {((entry.scores.visualFidelity + entry.scores.temporalConsistency + entry.scores.promptAdherence + entry.scores.overallUsability) / 4).toFixed(1)}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-[10px] px-2.5 py-1 rounded-full font-black uppercase tracking-tight ${entry.viability === 'As-is' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : entry.viability === 'Not Usable' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>{entry.viability}</span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {entry.assets?.length > 0 ? (
                                <div className="flex -space-x-2">
                                  {entry.assets.slice(0, 3).map((a, i) => (
                                    <a key={i} href={a.downloadURL} target="_blank" rel="noopener noreferrer" title="View Full File">
                                      <img src={a.thumbnailURL || a.downloadURL} className="w-7 h-7 rounded-md border-2 border-white dark:border-slate-900 object-cover hover:scale-125 hover:z-10 transition-transform cursor-pointer shadow-sm" />
                                    </a>
                                  ))}
                                  {entry.assets.length > 3 && <span className="text-[8px] bg-slate-200 dark:bg-slate-700 w-7 h-7 rounded-md flex items-center justify-center font-black">+{entry.assets.length - 3}</span>}
                                </div>
                              ) : <span className="text-slate-300">0</span>}
                            </div>
                          </td>
                          {!isLiteView && (
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2 text-slate-500">
                                <button 
                                  onClick={(e) => { e.stopPropagation(); toggleVisibility(entry); }}
                                  className={`p-2 rounded-lg transition-colors ${entry.visibility === 'external' ? 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                                >
                                  {entry.visibility === 'external' ? <ShieldCheck size={16}/> : <Lock size={16}/>}
                                </button>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); setDeleteId(entry.id); }}
                                  className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                >
                                  <Trash2 size={16}/>
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {entries.length === 0 && (
                    <div className="p-12 text-center text-slate-400">
                      <Activity className="mx-auto mb-4 opacity-20" size={48} />
                      <p className="text-sm font-medium">No results recorded yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TWO-PANEL LAYOUT: LOG TEST RESULT */}
          {activeTab === 'feedback' && !isLiteView && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                
                {/* LEFT PANEL: INTERNAL REVIEW (EDITABLE) */}
                <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden">
                  <div className="bg-slate-900 p-6 text-white">
                    <h2 className="text-xl font-black flex items-center gap-2 uppercase tracking-tight">
                      <Clipboard size={20} className="text-indigo-400"/> Internal Review
                    </h2>
                    <p className="text-[10px] text-slate-400 mt-1 font-bold uppercase tracking-[0.2em]">Mandatory Log (Not Shared Externally)</p>
                  </div>

                  <form onSubmit={handleSubmit} className="p-8 space-y-6">
                    {/* Basic Metadata Section */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Test ID</label>
                        <input type="text" readOnly value={formData.testId} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-xs font-mono text-slate-500" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Date</label>
                        <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-xs" />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Test Objective</label>
                      <input type="text" required placeholder="Describe the goal of this test..." value={formData.testObjective} onChange={e => setFormData({...formData, testObjective: e.target.value})} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-xs focus:ring-2 ring-indigo-500/20" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Model / Tool</label>
                        <input type="text" placeholder="Luma / Kling" value={formData.toolVersion} onChange={e => setFormData({...formData, toolVersion: e.target.value})} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-xs" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Iterations</label>
                        <input type="number" min="1" max="10" value={formData.iterations} onChange={e => setFormData({...formData, iterations: parseInt(e.target.value)})} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-xs" />
                      </div>
                    </div>

                    {/* Mandatory Analysis Text Areas */}
                    <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-1.5">
                           What Worked <span className="text-slate-300 lowercase font-normal">(required)</span>
                        </label>
                        <textarea rows="2" required value={formData.workedWell} onChange={e => setFormData({...formData, workedWell: e.target.value})} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-xs leading-relaxed focus:ring-2 ring-indigo-500/20" placeholder="Identity stayed strong in front view..."></textarea>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-red-600 uppercase tracking-widest flex items-center gap-1.5">
                           What Broke <span className="text-slate-300 lowercase font-normal">(required)</span>
                        </label>
                        <textarea rows="2" required value={formData.issues} onChange={e => setFormData({...formData, issues: e.target.value})} className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-xs leading-relaxed focus:ring-2 ring-indigo-500/20" placeholder="Significant artifacting during rotation..."></textarea>
                      </div>
                    </div>

                    {/* Quality Scores (Mandatory) */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                      {[
                        { k: 'visualFidelity', l: 'Visual Fidelity' },
                        { k: 'temporalConsistency', l: 'Temporal Consistency' },
                        { k: 'promptAdherence', l: 'Prompt Adherence' },
                        { k: 'overallUsability', l: 'Usability Score' }
                      ].map(score => (
                        <div key={score.k} className="space-y-1.5">
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{score.l}</label>
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map(v => (
                              <button key={v} type="button" onClick={() => setFormData({...formData, scores: {...formData.scores, [score.k]: v}})} className={`w-full h-8 rounded-lg text-[10px] font-black transition-all ${formData.scores[score.k] === v ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:bg-slate-200'}`}>{v}</button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Viability Assessment */}
                    <div className="space-y-2 pt-4 border-t border-slate-100 dark:border-slate-800">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Production Viability</label>
                      <div className="flex gap-2">
                        {['As-is', 'Light Editing', 'Not Usable'].map(v => (
                          <button key={v} type="button" onClick={() => setFormData({...formData, viability: v})} className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${formData.viability === v ? 'bg-slate-800 text-white shadow-lg' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:bg-slate-200'}`}>{v}</button>
                        ))}
                      </div>
                    </div>

                    {/* Asset Management */}
                    <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                       <div className="flex justify-between items-center mb-4">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Media Assets</label>
                          <label className={`text-[10px] font-black text-indigo-600 cursor-pointer flex items-center gap-1 hover:underline ${isUploading ? 'opacity-50' : ''}`}>
                            {isUploading ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>} Upload
                            <input type="file" className="hidden" accept="image/*,video/*" onChange={handleAssetUpload} disabled={isUploading} />
                          </label>
                       </div>
                       <div className="grid grid-cols-4 gap-3">
                          {formData.assets.map(asset => (
                            <div key={asset.id} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                              <img src={asset.thumbnailURL || asset.downloadURL} className="w-full h-full object-cover" />
                              <button 
                                type="button"
                                onClick={() => toggleAssetExternal(asset.id)}
                                className={`absolute bottom-1 right-1 p-1 rounded-md transition-all ${asset.marked_for_external ? 'bg-emerald-500 text-white shadow-lg' : 'bg-white/80 text-slate-400'}`}
                                title="Toggle External Visibility"
                              >
                                {asset.marked_for_external ? <Globe size={10}/> : <EyeOff size={10}/>}
                              </button>
                            </div>
                          ))}
                       </div>
                    </div>

                    {/* Visibility Gate & Save */}
                    <div className="pt-6 mt-6 border-t border-slate-100 dark:border-slate-800">
                      <div className="flex items-center justify-between mb-6 bg-indigo-50 dark:bg-indigo-900/10 p-4 rounded-2xl border border-indigo-100/50 dark:border-indigo-900/30">
                        <div>
                          <p className="text-[10px] font-black uppercase text-indigo-700 dark:text-indigo-400">External Visibility</p>
                          <p className="text-[9px] text-indigo-600/60 dark:text-indigo-400/60 mt-0.5">Approve to show in Lite Viewer</p>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            type="button"
                            disabled={!canApprove}
                            onClick={() => setFormData({...formData, visibility: formData.visibility === 'external' ? 'internal' : 'external'})}
                            className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all flex items-center gap-2 ${!canApprove ? 'opacity-30 cursor-not-allowed bg-slate-200 text-slate-400' : formData.visibility === 'external' ? 'bg-emerald-600 text-white shadow-lg' : 'bg-white text-indigo-600 shadow-sm'}`}
                          >
                            {formData.visibility === 'external' ? <CheckCircle size={14}/> : <Globe size={14}/>}
                            {formData.visibility === 'external' ? 'Approved' : 'Approve'}
                          </button>
                        </div>
                      </div>
                      
                      {!canApprove && (
                        <p className="text-[10px] text-amber-600 font-bold mb-4 flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 p-2.5 rounded-lg border border-amber-100/50">
                          <Info size={14}/> Complete all mandatory logs and select at least one external asset to enable approval.
                        </p>
                      )}

                      <button type="submit" disabled={isSubmitting || isUploading} className="w-full bg-slate-900 dark:bg-indigo-600 text-white py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-3 disabled:opacity-50">
                        {isSubmitting ? <Activity size={16} className="animate-spin" /> : <Save size={16} />} 
                        Commit Entry
                      </button>
                    </div>
                  </form>
                </div>

                {/* RIGHT PANEL: PHASE 1 LITE PREVIEW (READ ONLY) */}
                <div className="sticky top-28 bg-slate-100 dark:bg-slate-950/50 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-inner">
                  <div className="bg-slate-200 dark:bg-slate-900 p-6 flex justify-between items-center">
                    <div>
                      <h3 className="text-sm font-black uppercase tracking-tight text-slate-600 dark:text-slate-400">Phase 1 Lite Preview</h3>
                      <p className="text-[9px] text-slate-500 font-medium">How this appears to external viewers</p>
                    </div>
                    <Lock size={14} className="text-slate-400"/>
                  </div>

                  <div className="p-8 space-y-8 opacity-70 pointer-events-none select-none">
                    {/* Header Group */}
                    <div className="flex justify-between items-start border-b border-slate-200 dark:border-slate-800 pb-6">
                      <div>
                        <span className="text-[10px] font-mono font-bold text-indigo-600 mb-1 block">ID: {formData.testId}</span>
                        <h4 className="text-xl font-black tracking-tight">{formData.testObjective || "Test Objective Pending..."}</h4>
                      </div>
                      <div className="text-right">
                         <span className="text-[9px] font-black px-2.5 py-1 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 uppercase">{formData.toolVersion || "Model"}</span>
                      </div>
                    </div>

                    {/* Asset Showcase (Derived) */}
                    <div className="space-y-3">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Key Result Media</label>
                       <div className="grid grid-cols-2 gap-4">
                          {formData.assets.filter(a => a.marked_for_external).length > 0 ? (
                            formData.assets.filter(a => a.marked_for_external).slice(0, 2).map(a => (
                              <div key={a.id} className="aspect-video bg-slate-300 dark:bg-slate-800 rounded-2xl overflow-hidden relative border border-slate-200 dark:border-slate-700">
                                <img src={a.thumbnailURL || a.downloadURL} className="w-full h-full object-cover" />
                                {a.type === 'video' && <div className="absolute inset-0 flex items-center justify-center"><FileVideo size={24} className="text-white drop-shadow-lg opacity-50"/></div>}
                              </div>
                            ))
                          ) : (
                            <div className="col-span-2 aspect-video bg-slate-200 dark:bg-slate-800 rounded-3xl border-2 border-dashed border-slate-300 dark:border-slate-700 flex flex-col items-center justify-center text-slate-400">
                              <EyeOff size={24} className="mb-2 opacity-30"/>
                              <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">No External Assets Selected</p>
                            </div>
                          )}
                       </div>
                    </div>

                    {/* Derived Stats Group */}
                    <div className="grid grid-cols-2 gap-6">
                       <div className="p-5 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800">
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Overall Quality</p>
                          <div className="flex items-baseline gap-1">
                             <span className="text-3xl font-black text-indigo-600">{computedOverallScore}</span>
                             <span className="text-xs font-bold text-slate-300">/ 5.0</span>
                          </div>
                       </div>
                       <div className="p-5 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800">
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Suitability</p>
                          <span className={`text-lg font-black uppercase tracking-tighter ${formData.viability === 'As-is' ? 'text-emerald-600' : formData.viability === 'Not Usable' ? 'text-red-600' : 'text-amber-600'}`}>
                            {getSuitabilityLabel()}
                          </span>
                       </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                       <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                          <p className="text-[8px] font-black text-slate-400 uppercase mb-2">Visual Fidelity</p>
                          <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                             <div className="h-full bg-indigo-600" style={{width: `${(formData.scores.visualFidelity / 5) * 100}%`}}></div>
                          </div>
                       </div>
                       <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                          <p className="text-[8px] font-black text-slate-400 uppercase mb-2">Temporal Consistency</p>
                          <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                             <div className="h-full bg-indigo-600" style={{width: `${(formData.scores.temporalConsistency / 5) * 100}%`}}></div>
                          </div>
                       </div>
                    </div>

                    {/* Summary (Derived from viability) */}
                    <div className="pt-6 border-t border-slate-200 dark:border-slate-800">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Public Outcome Summary</p>
                      <div className="bg-indigo-600 text-white p-5 rounded-2xl shadow-xl shadow-indigo-600/20">
                        <p className="text-xs leading-relaxed font-bold italic">
                          {formData.viability === 'As-is' ? 
                            "This run demonstrates high production readiness. Key features remain consistent throughout motion sequences." :
                            formData.viability === 'Not Usable' ? 
                            "Significant failure modes identified during this run. Fundamental architectural limitations observed." :
                            "Testing indicates strong potential with minor technical refinements required before production deployment."
                          }
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 justify-center">
                       <span className="text-[8px] font-black uppercase tracking-widest text-slate-400">Visibility: {formData.visibility === 'external' ? 'Public Authorized' : 'Pending Internal Audit'}</span>
                    </div>

                  </div>
                  
                  {/* Visual indication of "Locked" Status */}
                  <div className="absolute top-4 right-4 bg-white/50 backdrop-blur-md px-3 py-1.5 rounded-full text-[9px] font-black uppercase border border-white/20 shadow-sm flex items-center gap-2 pointer-events-none">
                     <Lock size={10}/> Read Only
                  </div>
                </div>

              </div>
            </div>
          )}

          {activeTab === 'plan' && !isLiteView && (
            <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-700">
              <div className="bg-white dark:bg-slate-900 rounded-3xl p-10 border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-5"><Target size={200}/></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-5 mb-10">
                    <div className="bg-indigo-600 p-4 rounded-2xl text-white shadow-xl shadow-indigo-500/20"><Target size={32} /></div>
                    <h2 className="text-4xl font-black tracking-tighter">Phase 1 Workflow</h2>
                  </div>
                  <div className="grid gap-8">
                    {[
                      { step: 1, title: 'Select Characters', desc: 'Identify 1 "Easy" and 1 "Hard" character for baseline testing.' },
                      { step: 2, title: 'Lock Image Sets', desc: 'Curate exactly 6 fixed images for each selected character.' },
                      { step: 3, title: 'Run Canonical Set', desc: 'Apply Prompts 01–05 exactly. Do not vary prompt text between characters.' },
                      { step: 4, title: 'Log & Review', desc: 'Document results and mark viability. Use the "Lite" link for partner previews.' }
                    ].map(item => (
                      <div key={item.step} className="flex gap-6 items-start group">
                        <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-black text-indigo-600 shrink-0 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300 shadow-sm">{item.step}</div>
                        <div>
                          <h4 className="text-lg font-black tracking-tight mb-1">{item.title}</h4>
                          <p className="text-slate-500 text-sm leading-relaxed font-medium">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'doc' && (
  <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-6xl mx-auto">
    <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden print:shadow-none print:border-none">
      
      {/* 1. HEADER & PRINT CONTROL */}
      <div className="bg-slate-900 p-6 text-white flex justify-between items-center print:bg-white print:text-slate-900 print:border-b print:p-4">
        <div>
          <h2 className="text-xl font-black flex items-center gap-2 uppercase tracking-tight">
            <FileText size={20} className="text-indigo-400 print:text-indigo-600"/> Phase 1 Testing Authority Guide
          </h2>
          <p className="text-[10px] text-slate-400 mt-1 font-bold uppercase tracking-[0.2em] print:text-slate-500">
            Canonical Workflow & Character Standards • v1.2
          </p>
        </div>
        <button 
          onClick={() => window.print()} 
          className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition-colors print:hidden"
        >
          <Printer size={14} /> Print Reference
        </button>
      </div>

      <div className="p-8 space-y-8 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
        
        {/* SECTION 1: PURPOSE & GOALS */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          <div className="space-y-4">
            <h3 className="text-lg font-black uppercase tracking-tight border-b border-slate-100 dark:border-slate-800 pb-2 flex items-center gap-2">
              <Target size={18} className="text-indigo-500"/> Purpose & Strategy
            </h3>
            <p className="text-xs font-medium leading-relaxed">
              This document defines the complete Phase 1 testing setup for character-based video generation. 
              It is designed to keep testing controlled, comparable, and defensible while establishing a baseline before scaling characters.
            </p>
          </div>
          <div className="space-y-2 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-700">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-900 dark:text-white">High-Level Goals</h4>
            <ul className="text-[11px] space-y-1 font-bold list-disc pl-4">
              <li>Validate identity persistence under motion</li>
              <li>Identify early failure modes without noise from edge cases</li>
              <li>Test template behaviour using a small, repeatable dataset</li>
            </ul>
          </div>
        </section>

        {/* SECTION 2: CHARACTER DEFINITIONS */}
        <section className="space-y-6">
          <h3 className="text-lg font-black uppercase tracking-tight border-b border-slate-100 dark:border-slate-800 pb-2">1. Character Selection (Locked Set)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-5 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-2xl border border-indigo-100/50 dark:border-indigo-900/30">
              <h4 className="font-bold text-indigo-600 uppercase text-xs tracking-widest mb-3">Easy Character (Baseline Control)</h4>
              <ul className="space-y-1.5 text-[11px] mb-4">
                <li>• Neutral facial proportions and expressions</li>
                <li>• Even, soft lighting; clean, uncluttered background</li>
                <li>• No glasses, hats, or accessories</li>
                <li>• Standard camera angles (front-facing, 3/4)</li>
              </ul>
              <p className="text-[10px] italic border-t border-indigo-200/50 pt-2 mt-auto">Justification: If identity or motion fail here, the issue is fundamental rather than edge-case driven.</p>
            </div>
            <div className="p-5 bg-amber-50/50 dark:bg-amber-900/10 rounded-2xl border border-amber-100/50 dark:border-amber-900/30">
              <h4 className="font-bold text-amber-600 uppercase text-xs tracking-widest mb-3">Hard Character (Stress Test)</h4>
              <ul className="space-y-1.5 text-[11px] mb-4">
                <li>• Distinct facial structure or more complex hair</li>
                <li>• Higher contrast lighting (still consistent)</li>
                <li>• Subtle accessories (earrings, light makeup) allowed</li>
                <li>• Skin tones that historically show inconsistency</li>
              </ul>
              <p className="text-[10px] italic border-t border-amber-200/50 pt-2 mt-auto">Justification: Reveals whether failures scale with character complexity or appear randomly.</p>
            </div>
          </div>
        </section>

        {/* SECTION 3: IMAGE STANDARDS */}
        <section className="space-y-4">
          <h3 className="text-lg font-black uppercase tracking-tight border-b border-slate-100 dark:border-slate-800 pb-2">2. Mandatory Reference Images</h3>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">6 Images Per Character (12 Total for Phase 1)</p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              "1. Front Portrait", "2. 3/4 Portrait", "3. Full Body Front", 
              "4. Full Body 3/4", "5. Relaxed/Candid", "6. Motion Hint"
            ].map((img, i) => (
              <div key={i} className="p-3 bg-slate-900 text-white rounded-xl text-center">
                <p className="text-[9px] font-mono leading-tight">{img}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 dark:bg-slate-800/30 p-5 rounded-2xl border border-slate-100 dark:border-slate-700">
            <div>
              <h5 className="text-[10px] font-black uppercase mb-2 text-indigo-500">Image Standards</h5>
              <ul className="text-[11px] space-y-1">
                <li>• High-resolution, well-lit; no motion blur</li>
                <li>• Face clearly visible; no filters</li>
              </ul>
            </div>
            <div>
              <h5 className="text-[10px] font-black uppercase mb-2 text-red-500">What to Avoid</h5>
              <ul className="text-[11px] space-y-1">
                <li>• Mixed outfits, props, or background clutter</li>
                <li>• Strong expressions or exaggerated poses</li>
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 4: PROMPT SET */}
        <section className="space-y-6">
          <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-2">
            <h3 className="text-lg font-black uppercase tracking-tight">3. Canonical Phase 1 Prompt Set</h3>
            <span className="text-[10px] font-black bg-amber-100 text-amber-700 px-2 py-1 rounded">DO NOT TUN MID-RUN</span>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {[
              { id: '01', title: 'Static Baseline', focus: 'Identity Stability', text: 'A single adult person standing still, facing the camera. Neutral expression. Indoor setting with simple, even lighting. Medium shot from the waist up. Minimal movement.' },
              { id: '02', title: 'Subtle Motion', focus: 'Temporal Consistency', text: 'A single adult person standing and gently shifting their weight from one foot to the other. Neutral expression. Indoor setting with consistent lighting. Medium shot. Smooth, natural movement.' },
              { id: '03', title: 'Walking', focus: 'Gait & Physics', text: 'A single adult person walking slowly forward at a natural pace. Simple indoor or controlled outdoor environment. Full-body shot. Camera remains steady.' },
              { id: '04', title: 'Turn / Rotation', focus: '3D Coherence', text: 'A single adult person standing and slowly turning their body from facing forward to a three-quarter angle. Neutral expression. Simple indoor environment. Medium shot.' },
              { id: '05', title: 'Close-up', focus: 'Facial Stability', text: 'Close-up of a single adult person’s face. Neutral expression. Minimal head movement. Soft, even lighting. Camera remains still.' },
              { id: '06', title: 'Clothing Response', focus: 'Secondary Motion', text: 'A single adult person walking slowly while wearing loose clothing. Simple environment. Full-body shot. Natural movement. (Introduce only after Prompt 03 behaves predictably).' }
            ].map(p => (
              <div key={p.id} className="p-4 bg-white dark:bg-slate-950 rounded-xl border border-slate-100 dark:border-slate-800">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Prompt {p.id} — {p.title}</span>
                  <span className="text-[9px] font-mono text-slate-400 uppercase">Focus: {p.focus}</span>
                </div>
                <p className="text-xs font-mono bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800 leading-relaxed italic">"{p.text}"</p>
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 5: WORKFLOW */}
        <section className="space-y-6">
          <h3 className="text-lg font-black uppercase tracking-tight border-b border-slate-100 dark:border-slate-800 pb-2">4. Step-by-Step Testing Workflow</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative">
            <div className="space-y-6">
              {[
                { s: "Lock References", d: "Use same images across all prompts. Do not swap mid-cycle." },
                { s: "Max 4 Iterations", d: "Stop if acceptable. More than 4 iterations is a failure signal." },
                { s: "Standardized Logging", d: "Score 1-5, note timestamped issues, and iteration count." }
              ].map((step, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-black shrink-0">{i+1}</div>
                  <div>
                    <h5 className="text-xs font-black uppercase text-slate-900 dark:text-white">{step.s}</h5>
                    <p className="text-xs text-slate-500">{step.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-emerald-50/50 dark:bg-emerald-900/10 p-5 rounded-2xl border border-emerald-100 dark:border-emerald-900/30">
              <h5 className="text-[10px] font-black uppercase mb-3 text-emerald-600 tracking-widest flex items-center gap-2">
                <CheckCircle size={14}/> Passing Criteria
              </h5>
              <ul className="text-[11px] space-y-2 text-emerald-800 dark:text-emerald-400 font-bold uppercase tracking-tight">
                <li>• Acceptable output within 2-4 iterations</li>
                <li>• No major visual breakdowns mid-clip</li>
                <li>• Character remains recognisable throughout</li>
                <li>• Clip is usable with light editing</li>
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 6: NON-GOALS */}
        <section className="p-6 bg-slate-900 text-slate-400 rounded-3xl">
          <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-white mb-4">Phase 1 Explicit Non-Goals</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-[11px] font-bold">
            <p>• Multi-character interaction</p>
            <p>• Cinematic camera work</p>
            <p>• Long-form clips</p>
            <p>• Automation or API workflows</p>
            <p>• Production speed or cost</p>
            <p>• Narrative complexity</p>
          </div>
        </section>

      </div>

      {/* Footer */}
      <footer className="bg-slate-50 dark:bg-slate-800/50 p-6 flex justify-between items-center border-t border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white text-[10px] font-black">P1</div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Internal Protocol</p>
            <p className="text-[9px] font-mono text-slate-400">REF: P1-BETA-PROTOCOL-V1.2</p>
          </div>
        </div>
        <p className="text-[9px] font-bold text-slate-400 uppercase">Standard Operating Procedure</p>
      </footer>
    </div>
  </div>
)}
        </main>

        {selectedEntry && (
          <ResultDrawer
            entry={selectedEntry}
            onClose={closeDrawer}
            onNext={goNext}
            onPrevious={goPrevious}
            hasNext={selectedIndex < entries.length - 1}
            hasPrevious={selectedIndex > 0}
            isLiteView={isLiteView}
          />
        )}

      </div>

      {/* Print-friendly summary for both internal and lite views */}
      <div id="print-summary" className="p-8 text-slate-900">
        <h2 className="text-xl font-black mb-1">Results Repository (Print)</h2>
        <p className="text-xs text-slate-500 mb-6">View: {isLiteView ? 'Lite (external-safe)' : 'Internal'} · Total entries: {entries.length}</p>
        <div className="space-y-4">
          {entries.map((entry, idx) => (
            <div key={entry.id || idx} className="border border-slate-200 rounded-xl p-4 print-row">
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3 text-xs font-semibold">
                  <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded">{entry.testId || '—'}</span>
                  <span className="text-slate-500">{entry.date || '—'}</span>
                  <span className="px-2 py-1 rounded bg-slate-100 text-slate-700">{entry.viability || '—'}</span>
                  <span className="px-2 py-1 rounded bg-slate-100 text-slate-700">Score: {getAverageScore(entry)}</span>
                  {entry.clipLength && <span className="px-2 py-1 rounded bg-slate-100 text-slate-700">{entry.clipLength}</span>}
                  {!isLiteView && entry.iterations !== undefined && <span className="text-slate-500">Iter: {entry.iterations}</span>}
                </div>
                {entry.assets?.length > 0 && (
                  <div className="flex items-center gap-2">
                    {entry.assets.slice(0, 2).map((a, i) => (
                      <img
                        key={i}
                        src={a.thumbnailURL || a.downloadURL}
                        alt={a.name || `asset-${i}`}
                        className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-2 text-sm font-semibold text-slate-800">{entry.testObjective || '—'}</div>
              {!isLiteView && (
                <div className="mt-1 text-xs text-slate-500">Model: {entry.toolVersion || '—'}</div>
              )}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-slate-700">
                {!isLiteView && (
                  <div>
                    <div className="font-semibold text-slate-800">Internal Notes</div>
                    <div className="mt-1 whitespace-pre-line">{entry.workedWell || '—'}</div>
                    <div className="mt-1 whitespace-pre-line">{entry.issues || '—'}</div>
                  </div>
                )}
                <div>
                  <div className="font-semibold text-slate-800">External Summary</div>
                  <div className="mt-1 whitespace-pre-line">{entry.externalSummaryText || '—'}</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-600 flex items-center gap-2">
                <span className="font-semibold">Assets:</span>
                <span>{entry.assets?.length || 0}</span>
                {entry.assets?.slice(0, 3).map((a, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">{a.name || a.type || 'asset'}</span>
                ))}
                {entry.assets && entry.assets.length > 3 && (
                  <span className="px-2 py-0.5 rounded bg-slate-200 text-slate-700">+{entry.assets.length - 3} more</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const root = document.getElementById('root');
if (root) {
  ReactDOM.createRoot(root).render(<App />);
}

export default App;